import React, { useState } from "react";
import { Button, Drawer, message, Space, Table, Tag } from "antd";
import { useLazyQuery, useQuery } from "@apollo/client";
import type { ColumnsType } from "antd/es/table";
import {
  GET_DSP_ADJUST_BILL_TASKS,
  GetDspAdjustBillTasksData,
  GetDspAdjustBillTasksVariables,
} from "../graphql/queries/getDspAdjustBillTasks";
import { DspAdjustBillTask } from "../graphql/queries/getDspAdjustBillTask";
import {
  GET_DSP_ADJUST_BILL_DETAILS_CSV_URL,
  GetDspAdjustBillDetailsCsvUrlData,
  GetDspAdjustBillDetailsCsvUrlVariables,
} from "../graphql/queries/getDspAdjustBillDetailsCsvUrl";
import { DspAdjustBillUploadModal } from "./DspAdjustBillUploadModal";
import { DspAdjustBillTaskModal } from "./DspAdjustBillTaskModal";
import { HasResource } from "components/HasResource";
import { ResourceCode } from "../../../../constants/resourceCodes";

interface Props {
  billId: string;
  visible: boolean;
  onClose: () => void;
  isTaskInProgress: boolean;
}

const STATUS_TAG: Record<number, React.ReactNode> = {
  0: <Tag color="processing">处理中</Tag>,
  1: <Tag color="processing">处理中</Tag>,
  2: <Tag color="success">已完成</Tag>,
  3: <Tag color="error">失败</Tag>,
  4: <Tag color="error">失败</Tag>,
};

export const DspAdjustBillHistoryDrawer: React.FC<Props> = ({
  billId,
  visible,
  onClose,
  isTaskInProgress,
}) => {
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<DspAdjustBillTask | null>(null);

  const { data, loading, refetch } = useQuery<
    GetDspAdjustBillTasksData,
    GetDspAdjustBillTasksVariables
  >(GET_DSP_ADJUST_BILL_TASKS, {
    variables: { billId },
    skip: !visible,
    fetchPolicy: "network-only",
  });

  const [fetchCsvUrl, { loading: csvLoading }] = useLazyQuery<
    GetDspAdjustBillDetailsCsvUrlData,
    GetDspAdjustBillDetailsCsvUrlVariables
  >(GET_DSP_ADJUST_BILL_DETAILS_CSV_URL, {
    fetchPolicy: "no-cache",
    onCompleted: (d) => window.open(d.getDspAdjustBillDetailsCsvUrl, "_blank"),
    onError: (err) => message.error("导出失败: " + err.message),
  });

  const tasks = data?.getDspAdjustBillTasks ?? [];

  const columns: ColumnsType<DspAdjustBillTask> = [
    { title: "任务ID", dataIndex: "id", key: "id", width: 90 },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 90,
      render: (s: number) => STATUS_TAG[s] ?? <Tag>未知</Tag>,
    },
    {
      title: "成功 / 失败",
      key: "counts",
      width: 110,
      render: (_: unknown, r: DspAdjustBillTask) => `${r.successCount} / ${r.failCount}`,
    },
    {
      title: "提交时间(America/Los_Angeles)",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 160,
      render: (v: string | null) => v ?? "-",
    },
    {
      title: "完成时间(America/Los_Angeles)",
      dataIndex: "finishedTime",
      key: "finishedTime",
      width: 160,
      render: (v: string | null) => v ?? "-",
    },
    {
      title: "操作",
      key: "actions",
      width: 150,
      fixed: "right" as const,
      render: (_: unknown, r: DspAdjustBillTask) => (
        <Space>
          {/* <Button type="link" size="small" onClick={() => setSelectedTask(r)}>
            查看详情
          </Button> */}
          <Button
            type="link"
            size="small"
            loading={csvLoading}
            onClick={() => fetchCsvUrl({ variables: { taskId: r.id } })}
          >
            导出明细
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Drawer
        title="明细调账历史"
        open={visible}
        onClose={onClose}
        width={900}
        extra={
          <Button
            type="primary"
            onClick={() => setUploadModalVisible(true)}
          >
            新建调账
          </Button>
        }
      >
        <Table<DspAdjustBillTask>
          rowKey="id"
          loading={loading}
          dataSource={tasks}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ x: 760 }}
        />
      </Drawer>
      <DspAdjustBillUploadModal
        billId={billId}
        visible={uploadModalVisible}
        onClose={() => setUploadModalVisible(false)}
        onSuccess={() => refetch()}
      />
      {selectedTask && (
        <DspAdjustBillTaskModal
          task={selectedTask}
          visible={true}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </>
  );
};
